import { useState } from "react";
import axios from "axios";
import { useAuth, useRoleLabel } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { Eye, CaretDown, Crown, ShieldCheck, User, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

import { API_BASE as API } from "../lib/apiBase";

/**
 * RoleSwitcher — "Switch Profils Utilisateur" (View-As)
 * Lets founders/admins simulate a lower role to test what other users see.
 * The real role is preserved server-side; switching here only affects the UI.
 */
export default function RoleSwitcher() {
  const { user, refreshUser } = useAuth();
  const { lang } = useLang();
  const roleLabel = useRoleLabel();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Tâche 1442 (Eric 2026-10-02) — Les concepteurs ont aussi la bascule de
  // vue, limitée aux rôles STRICTEMENT inférieurs au leur.
  // La liste permise est calculée AU SERVEUR (`view_as_allowed`, posé par
  // `core/auth.vues_autorisees`) : ce qui est filtré ici n'est qu'un confort,
  // le serveur refuse de toute façon une vue non permise. Le repli couvre le
  // cas d'un jeton émis avant cette tâche, où le champ n'existe pas encore.
  const vuesPermises = user?.view_as_allowed;
  const isRealAdmin = user?.is_simulating
    ? user?.real_role === "admin" || user?.real_is_founder
    : user?.role === "admin" || user?.is_founder;
  const peutBasculer = Array.isArray(vuesPermises)
    ? vuesPermises.length > 0
    : isRealAdmin;
  if (!peutBasculer) return null;

  const current = user?.is_simulating ? user?.view_as_role : "real";

  const switchTo = async (role) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/auth/view-as`,
        { role: role === "real" ? null : role },
        { withCredentials: true }
      );
      await refreshUser();
      // Eric / Vincent (2026-05-15, bug #122 prod) — Emit a global event so
      // EVERY page that depends on the role/visibility (KB, Procédures, etc.)
      // refetches its data, without having to remember to add `view_as_role`
      // to its useEffect deps. This is a structural defense against the
      // "I forgot a useEffect dep" class of regressions.
      try {
        window.dispatchEvent(new CustomEvent("knoria:user-changed", { detail: { reason: "view-as", role } }));
      } catch (_) { /* old browser */ }
      const opt = options.find((o) => o.id === role);
      toast.success((lang === "fr" ? "Bascule vers " : "Switched to ") + (opt?.label || role));
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "fr" ? "Erreur" : "Error"));
    } finally {
      setBusy(false);
    }
  };

  const isFounder = user?.real_is_founder || user?.is_founder;
  // Vincent #98 — Use custom role labels from the org (set via Admin > Gestion des rôles)
  // so the View-As dropdown reflects FLHQ's "Conseiller", "Stagiaire" etc.
  // Vincent #259 (2026-02-06) — Pourcentages retirés : ils n'étaient plus
  // pertinents depuis l'introduction de la Sélection des accès (matrice par
  // case cochée), et donnaient une fausse impression de hiérarchie chiffrée.
  const viewPrefix = lang === "en" ? "View: " : "Vue : ";
  // Vincent B875 v2 (Eric 2026-02-14) — Couleurs remontées de -400/-500
  // vers -700/-800 pour passer WCAG AA sur bg-secondary/-zinc-100
  // (l'ancien text-teal-400 sur zinc-100 donnait 3.5:1 < 4.5).
  const toutesLesOptions = lang === "en" ? [
    { id: "real", label: isFounder ? `${viewPrefix}${roleLabel("founder")} (real)` : `${viewPrefix}${roleLabel("admin")} (real)`, icon: Crown, color: "text-cyan-700", desc: "Full access" },
    { id: "admin", label: `${viewPrefix}${roleLabel("admin")}`, icon: ShieldCheck, color: "text-amber-700", desc: "Without Platform KNORIA" },
    { id: "user:concepteur", label: `${viewPrefix}${roleLabel("concepteur")}`, icon: ShieldCheck, color: "text-violet-700", desc: "Pedagogue — General + Designers" },
    { id: "user:avance", label: `${viewPrefix}${roleLabel("employee_advanced")}`, icon: User, color: "text-teal-800", desc: "+ upload, explorer" },
    { id: "user:intermediaire", label: `${viewPrefix}${roleLabel("employee_trained")}`, icon: User, color: "text-teal-700", desc: "+ knowledge, agora" },
    { id: "user:debutant", label: `${viewPrefix}${roleLabel("employee_new")}`, icon: User, color: "text-teal-700", desc: "Discovery, chat, quiz" },
  ] : [
    { id: "real", label: isFounder ? `${viewPrefix}${roleLabel("founder")} (réel)` : `${viewPrefix}${roleLabel("admin")} (réel)`, icon: Crown, color: "text-cyan-700", desc: "Accès total" },
    { id: "admin", label: `${viewPrefix}${roleLabel("admin")}`, icon: ShieldCheck, color: "text-amber-700", desc: "Sans Platform KNORIA" },
    { id: "user:concepteur", label: `${viewPrefix}${roleLabel("concepteur")}`, icon: ShieldCheck, color: "text-violet-700", desc: "Pédagogue — Général + Concepteurs" },
    { id: "user:avance", label: `${viewPrefix}${roleLabel("employee_advanced")}`, icon: User, color: "text-teal-800", desc: "+ upload, explorateur" },
    { id: "user:intermediaire", label: `${viewPrefix}${roleLabel("employee_trained")}`, icon: User, color: "text-teal-700", desc: "+ connaissances, agora" },
    { id: "user:debutant", label: `${viewPrefix}${roleLabel("employee_new")}`, icon: User, color: "text-teal-700", desc: "Découverte, chat, quiz" },
  ];

  // Tâche 1442 — « real » reste TOUJOURS proposé : sortir de simulation ne
  // doit jamais être interdit, sinon une personne dont le rôle a été réduit
  // pendant qu'elle simulait resterait enfermée dans la vue d'un autre.
  const options = Array.isArray(vuesPermises)
    ? toutesLesOptions.filter((o) => o.id === "real" || vuesPermises.includes(o.id))
    : toutesLesOptions;

  const currentOpt = options.find((o) => o.id === current) || options[0];
  const Icon = currentOpt.icon;
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-2.5 h-8 rounded-lg text-xs font-medium transition-colors border ${
          user?.is_simulating
            ? "bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border-amber-300/50 hover:bg-amber-200 dark:hover:bg-amber-950/60"
            : "bg-secondary text-zinc-800 border-transparent hover:bg-secondary/80"
        }`}
        data-testid="role-switcher-btn"
        title={lang === "fr" ? "Bascule de profil utilisateur" : "User profile switcher"}
      >
        <Eye size={14} weight={user?.is_simulating ? "fill" : "regular"} />
        <span className="hidden sm:inline max-w-[110px] truncate">{currentOpt.label}</span>
        <CaretDown size={10} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 top-full mt-1.5 w-72 bg-card border border-border rounded-xl shadow-2xl z-50 overflow-hidden"
            data-testid="role-switcher-dropdown"
          >
            <div className="px-3 py-2 border-b border-border bg-secondary/40">
              <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                {lang === "fr" ? "Bascule de profil utilisateur" : "User profile switcher"}
              </div>
              <div className="text-[11px] text-muted-foreground/80 mt-0.5">
                {lang === "fr" ? "Testez ce que voient les autres rôles" : "Test what other roles see"}
              </div>
            </div>
            <div className="py-1">
              {options.map((opt) => {
                const OptIcon = opt.icon;
                const active = current === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => switchTo(opt.id)}
                    disabled={busy || active}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-secondary transition-colors text-left disabled:opacity-60 ${
                      active ? "bg-secondary/60" : ""
                    }`}
                    data-testid={`role-option-${opt.id}`}
                  >
                    <OptIcon size={16} weight="duotone" className={opt.color} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium leading-tight">{opt.label}</div>
                      {opt.desc && <div className="text-[10px] text-muted-foreground mt-0.5">{opt.desc}</div>}
                    </div>
                    {active && <Check size={14} className="text-teal-500 flex-shrink-0" weight="bold" />}
                  </button>
                );
              })}
            </div>
            {user?.is_simulating && (
              <div className="px-3 py-2 border-t border-border bg-amber-50 dark:bg-amber-950/30 text-[11px] text-amber-700 dark:text-amber-300">
                {lang === "fr"
                  ? "Mode simulation actif — les permissions affichées ne sont pas vos vraies permissions."
                  : "Simulation mode active — the permissions shown are not your real permissions."}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
